import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChartLineUp, TrendUp, TrendDown, Globe } from "@phosphor-icons/react";
import { fetchMovers } from "../lib/api";
import SEO from "../components/SEO";

const REGIONS = ["Global", "USA", "India", "Europe", "Asia"];
const ASSET_CLASSES = [
  { key: "all", label: "All" },
  { key: "stocks", label: "Stocks" },
  { key: "crypto", label: "Crypto" },
  { key: "commodities", label: "Commodities" },
  { key: "indices", label: "Indices" },
];

// Single component handles 3 route shapes:
//   /most-bullish, /most-bearish, /movers/:region
// `defaultDirection` picks bullish vs bearish; route param sets initial region.
export default function Screener({ defaultDirection = "bullish" }) {
  const navigate = useNavigate();
  const params = useParams();
  const [direction, setDirection] = useState(defaultDirection);
  const [region, setRegion] = useState(params.region ? capitalize(params.region) : "Global");
  const [assetClass, setAssetClass] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchMovers({ region, direction, asset_class: assetClass, limit: 25 })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [region, direction, assetClass]);

  const items = data?.items || [];
  const directionLabel = direction === "bullish" ? "Most Bullish" : "Most Bearish";
  const directionColor = direction === "bullish" ? "#34C759" : "#FF3B30";
  const DirIcon = direction === "bullish" ? TrendUp : TrendDown;

  const title = `${directionLabel} ${assetClass === "all" ? "Assets" : ASSET_CLASSES.find(c => c.key === assetClass)?.label} in ${region}`;
  const description =
    direction === "bullish"
      ? `Top 25 most bullish ${assetClass === "all" ? "assets" : assetClass} right now in ${region}, ranked by Sent-i-meter's composite sentiment score (technicals + momentum + news + volatility). Updated daily.`
      : `Top 25 most bearish ${assetClass === "all" ? "assets" : assetClass} right now in ${region}, ranked by Sent-i-meter's composite sentiment score. Updated daily.`;

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="screener-page">
      <SEO
        title={title}
        path={direction === "bullish" ? "/most-bullish" : "/most-bearish"}
        description={description}
        keywords={[
          `most ${direction} stocks`,
          `top ${direction} ${assetClass}`,
          `${region.toLowerCase()} ${direction} sentiment`,
          "stock movers today",
          "market sentiment screener",
          `${direction} stocks ${region.toLowerCase()}`,
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: title,
          description,
          numberOfItems: items.length,
          itemListElement: items.slice(0, 10).map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://sent-i-meter.com/asset/${encodeURIComponent(it.symbol)}`,
            name: `${it.name} (${it.symbol})`,
          })),
        }}
      />

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="screener-back">
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
            <ChartLineUp size={16} weight="bold" />
          </button>
          <div className="hidden sm:block">
            <div className="heading text-sm font-bold tracking-tight leading-none">SCREENER</div>
            <div className="overline mt-0.5 leading-none">live sentiment movers</div>
          </div>
        </div>
        <DirIcon size={18} weight="duotone" color={directionColor} />
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1200px] mx-auto">
        {/* Hero */}
        <section className="bg-surface border border-white/10 p-5 mb-5" data-testid="screener-hero">
          <div className="overline mb-1">
            {data?.as_of ? `updated ${new Date(data.as_of).toUTCString()}` : "loading"}
          </div>
          <h1 className="heading text-3xl md:text-4xl font-bold tracking-tight" style={{ color: directionColor }}>
            {directionLabel}
          </h1>
          <p className="text-sm text-white/70 mt-2 max-w-2xl">{description}</p>
        </section>

        {/* Filter rows */}
        <section className="bg-surface border border-white/10 p-4 mb-5 flex flex-wrap items-center gap-4" data-testid="screener-filters">
          <div>
            <div className="overline mb-1.5">direction</div>
            <div className="flex gap-1">
              {["bullish", "bearish"].map((d) => (
                <button
                  key={d}
                  onClick={() => setDirection(d)}
                  className={`px-2.5 py-1.5 mono text-[10px] uppercase tracking-[0.14em] border ${
                    direction === d ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                  }`}
                  data-testid={`screener-dir-${d}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-1.5">region</div>
            <div className="flex flex-wrap gap-1">
              {REGIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  className={`px-2 py-1 mono text-[10px] uppercase tracking-[0.14em] border ${
                    region === r ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                  }`}
                  data-testid={`screener-region-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-1.5">asset class</div>
            <div className="flex flex-wrap gap-1">
              {ASSET_CLASSES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setAssetClass(c.key)}
                  className={`px-2 py-1 mono text-[10px] uppercase tracking-[0.14em] border ${
                    assetClass === c.key ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                  }`}
                  data-testid={`screener-class-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Ranked list */}
        <section data-testid="screener-list">
          {loading && <div className="overline py-12 text-center">loading…</div>}
          {!loading && items.length === 0 && (
            <div className="bg-surface border border-white/10 p-8 text-center overline">no matches</div>
          )}
          {!loading && items.length > 0 && (
            <div className="bg-surface border border-white/10 divide-y divide-white/5">
              {items.map((it, i) => {
                const cellColor = it.composite >= 60 ? "#34C759" : it.composite <= 40 ? "#FF3B30" : "#FFCC00";
                return (
                  <button
                    key={it.symbol}
                    onClick={() => navigate(`/asset/${encodeURIComponent(it.symbol)}`)}
                    className="w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-white/[0.03] transition-colors duration-150"
                    data-testid={`mover-${it.symbol}`}
                  >
                    <span className="mono text-xs text-white/40 w-6 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="heading text-sm font-semibold">{it.symbol}</span>
                        <span className="text-sm text-white/70 truncate">{it.name}</span>
                      </div>
                      <div className="overline mt-0.5">
                        {it.region}
                        {it.sector && <span> · {it.sector}</span>}
                        {it.asset_class && <span> · {it.asset_class}</span>}
                      </div>
                    </div>
                    {it.change_pct != null && (
                      <span className="mono text-xs" style={{ color: it.change_pct >= 0 ? "#34C759" : "#FF3B30" }}>
                        {it.change_pct >= 0 ? "+" : ""}{it.change_pct.toFixed(2)}%
                      </span>
                    )}
                    <span className="mono text-sm font-bold" style={{ color: cellColor, minWidth: 40, textAlign: "right" }}>
                      {Math.round(it.composite)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="overline hover:text-white">
            ← back to dashboard
          </button>
          <span className="overline flex items-center gap-1.5"><Globe size={11} weight="bold" /> updated daily · not financial advice</span>
        </footer>
      </main>
    </div>
  );
}

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : "";
}
